/**
 * Shared network clients for the ai-assist app's LLM backend calls
 *
 * These clients are used by both the Next.js app and the Vite web component build.
 * They work in browser environments (using globalThis.fetch).
 */

import { createClient, createTracingFetch } from "@workspace/network";

/**
 * Retry budget for a gateway cold start.
 *
 * The gateway answers `503 service_starting` with `Retry-After: 3` while it
 * boots a lazily-started service, and the client honours that header — so this
 * mostly waits exactly as long as the gateway asks. Three attempts with
 * backoff covers a typical Node service start without making a genuinely down
 * service take half a minute to report itself.
 *
 * ONLY for idempotent reads. Never apply it to a chat completion: retrying one
 * bills twice and can deliver two replies.
 */
export const COLD_START_RETRY = {
  attempts: 3,
  delay: 1_000,
  backoff: true,
  maxDelay: 8_000,
} as const;

const tracedFetch = createTracingFetch({
  onRequest: (url, method) => console.log(`[ai-assist] → ${method} ${url}`),
  onResponse: (url, method, status, ms) => console.log(`[ai-assist] ← ${status} ${method} ${url} (${ms}ms)`),
  onError: (url, method, err, ms) => console.error(`[ai-assist] ✗ ${method} ${url} (${ms}ms): ${err}`),
  infraViz: { source: "ai-assist" },
});

/**
 * Client for LLM API calls (models, tools listing, streaming chat)
 * - Moderate timeout for API calls
 * - No retries BY DEFAULT: a chat completion is not idempotent, and retrying
 *   one bills twice and can deliver two replies.
 *
 * Read-only callers opt in per request instead — see COLD_START_RETRY below.
 * Backend services are lazily started by the gateway, so the first call after
 * an idle period gets `503 service_starting` while the process boots; without
 * a retry that surfaces as a hard failure for something that would have
 * worked a second later.
 */
export const llmApiClient = createClient({
  timeout: 30_000,
  retry: { attempts: 0 },
  // Send the auth cookie so the LB can identify the user — required for
  // centralized Brain recall (tdd-0042), which gates on the authenticated user.
  credentials: "include",
  fetch: tracedFetch,
});

/**
 * Client for file upload presign requests
 * - Short timeout
 * - No retries
 */
export const uploadClient = createClient({
  timeout: 10_000,
  // The LB gates /files/presign (minting an upload slot is an action, not a
  // read), so the presign must carry the session cookie like chat's other calls.
  credentials: "include",
  // Presigning is idempotent — it mints a URL, it does not upload — so a
  // cold-start 503 is safe to ride out.
  retry: COLD_START_RETRY,
  fetch: tracedFetch,
});
